// Per-project intake form management section — rendered inside ProjectSettingsPanel.
// Features: list forms (name, enabled badge, field count), one-time share-link display
// with copy button after create/rotate, enable toggle, rotate-token, delete with confirm.
// Add-form: name, field-config builder (key/label/type/required rows), target-phase
// picker from the passed phases array, enabled toggle (default true).

import { useState } from "preact/hooks";
import { Copy, Link, Plus, RefreshCw, Trash2 } from "lucide-preact";
import type { ApiIntakeField, Phase } from "@juuplan/shared";
import { toastSuccess } from "../lib/toast";
import {
  useIntakeForms,
  addIntakeForm,
  patchIntakeForm,
  rotateIntakeForm,
  removeIntakeForm,
} from "../store/intakeForms";

import { Button } from "./ui/Button";

interface Props {
  projectId: number;
  /** When false the mutating controls are hidden (view-only role). */
  canEdit: boolean;
  /** Project phases, used for the target-phase picker. */
  phases: Phase[];
}

type FieldType = "text" | "textarea" | "email" | "number";

const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: "Text",
  textarea: "Mehrzeiliger Text",
  email: "E-Mail",
  number: "Zahl",
};

const FIELD_TYPES: FieldType[] = ["text", "textarea", "email", "number"];

const SELECT_CLS =
  "w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-base sm:text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2";
const INPUT_CLS =
  "w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-base sm:text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2";

/** Produces the full URL for a share_path, prefixing with current origin. */
function buildShareUrl(sharePath: string): string {
  if (typeof window !== "undefined") {
    return `${window.location.origin}${sharePath}`;
  }
  return sharePath;
}

/** Copies text to clipboard and returns whether it succeeded. */
async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Fallback for browsers without clipboard API
    const el = document.createElement("textarea");
    el.value = text;
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
  }
}

/** Inline component that shows a share URL with a copy button. */
function ShareLinkDisplay(props: { url: string }): preact.JSX.Element {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (): Promise<void> => {
    await copyToClipboard(props.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div class="flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2 py-1.5">
      <Link size={14} class="shrink-0 text-[color:var(--color-on-surface-variant)]" aria-hidden="true" />
      <span class="flex-1 min-w-0 text-label text-[color:var(--color-on-surface)] break-all select-all">
        {props.url}
      </span>
      <button
        type="button"
        onClick={() => void handleCopy()}
        title="Link kopieren"
        aria-label="Link kopieren"
        class="shrink-0 rounded p-0.5 text-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-container)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)] transition-colors"
      >
        <Copy size={14} aria-hidden="true" />
      </button>
      {copied && (
        <span class="shrink-0 text-label text-[color:var(--color-primary)]">
          Kopiert!
        </span>
      )}
    </div>
  );
}

/** Blank field row used to initialise the add-form builder. */
function blankField(): ApiIntakeField {
  return { key: "", label: "", type: "text", required: false };
}

/**
 * Per-project intake form management list.
 * Renders inside ProjectSettingsPanel as a section below AutomationRulesSection.
 */
export function IntakeFormsSection(props: Props): preact.JSX.Element {
  const { projectId, canEdit, phases } = props;
  const { items, loading, error } = useIntakeForms(projectId);

  // Per-form state maps
  const [confirmDel, setConfirmDel] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState<Set<number>>(new Set());
  // Transient one-time share URLs keyed by form id
  const [shareUrls, setShareUrls] = useState<Map<number, string>>(new Map());

  // Add-form state
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newFields, setNewFields] = useState<ApiIntakeField[]>([blankField()]);
  const [newPhaseId, setNewPhaseId] = useState<string>("");
  const [newEnabled, setNewEnabled] = useState(true);
  const [creating, setCreating] = useState(false);

  // ---- helpers ----

  const setBusyId = (id: number, on: boolean): void => {
    setBusy((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const storeShareUrl = (formId: number, sharePath: string): void => {
    setShareUrls((prev) => {
      const next = new Map(prev);
      next.set(formId, buildShareUrl(sharePath));
      return next;
    });
  };

  const clearShareUrl = (formId: number): void => {
    setShareUrls((prev) => {
      const next = new Map(prev);
      next.delete(formId);
      return next;
    });
  };

  const toggleEnabled = async (formId: number, currentEnabled: boolean): Promise<void> => {
    setBusyId(formId, true);
    try {
      await patchIntakeForm(formId, projectId, { enabled: !currentEnabled });
      toastSuccess(currentEnabled ? "Formular deaktiviert." : "Formular aktiviert.");
    } catch {
      // api wrapper already toasted
    } finally {
      setBusyId(formId, false);
    }
  };

  const handleRotate = async (formId: number): Promise<void> => {
    setBusyId(formId, true);
    clearShareUrl(formId);
    try {
      const result = await rotateIntakeForm(formId, projectId);
      storeShareUrl(formId, result.share_path);
      toastSuccess("Link erneuert.");
    } catch {
      // api wrapper already toasted
    } finally {
      setBusyId(formId, false);
    }
  };

  const handleDelete = async (formId: number): Promise<void> => {
    setBusyId(formId, true);
    try {
      await removeIntakeForm(formId, projectId);
      clearShareUrl(formId);
      setConfirmDel((prev) => {
        const next = new Set(prev);
        next.delete(formId);
        return next;
      });
      toastSuccess("Formular gelöscht.");
    } catch {
      // api wrapper already toasted
    } finally {
      setBusyId(formId, false);
    }
  };

  // ---- field builder helpers ----

  const updateField = (idx: number, patch: Partial<ApiIntakeField>): void => {
    setNewFields((prev) =>
      prev.map((f, i) => (i === idx ? { ...f, ...patch } : f))
    );
  };

  const addField = (): void => {
    setNewFields((prev) => [...prev, blankField()]);
  };

  const removeField = (idx: number): void => {
    setNewFields((prev) => prev.filter((_, i) => i !== idx));
  };

  const resetNewForm = (): void => {
    setNewName("");
    setNewFields([blankField()]);
    setNewPhaseId("");
    setNewEnabled(true);
  };

  const isNewFormSubmittable = (): boolean => {
    if (!newName.trim()) return false;
    // Each field must have both key and label filled
    for (const f of newFields) {
      if (!f.key.trim() || !f.label.trim()) return false;
    }
    return newFields.length > 0;
  };

  const submitNew = async (): Promise<void> => {
    if (!isNewFormSubmittable()) return;
    setCreating(true);
    try {
      const result = await addIntakeForm(projectId, {
        name: newName.trim(),
        field_config: newFields.map((f) => ({
          ...f,
          key: f.key.trim(),
          label: f.label.trim(),
        })),
        target_phase_id: newPhaseId ? parseInt(newPhaseId, 10) : null,
        enabled: newEnabled,
      });
      // Store the one-time share URL keyed by the new form's id
      storeShareUrl(result.id, result.share_path);
      resetNewForm();
      setShowNew(false);
      toastSuccess("Formular angelegt.");
    } catch {
      // api wrapper already toasted
    } finally {
      setCreating(false);
    }
  };

  // ---- render ----

  return (
    <div class="flex flex-col gap-2">
      <div class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
        Eingangsformulare
      </div>

      {loading && (
        <p class="text-xs text-[color:var(--color-on-surface-variant)]">Lädt…</p>
      )}

      {error && (
        <p class="text-xs text-[color:var(--color-error)]">Fehler: {error}</p>
      )}

      {!loading && !error && items.length === 0 && (
        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
          Noch keine Eingangsformulare.
        </p>
      )}

      {!loading &&
        items.map((form) => {
          const isBusy = busy.has(form.id);
          const isConfirmDel = confirmDel.has(form.id);
          const sharedUrl = shareUrls.get(form.id);
          const targetPhase = phases.find((p) => p.id === form.target_phase_id);

          return (
            <div
              key={form.id}
              class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-hidden"
            >
              {/* Form summary row */}
              <div class="flex items-start gap-2 px-2 py-1.5">
                <div class="flex-1 min-w-0">
                  <p class="text-xs text-[color:var(--color-on-surface)] font-medium truncate">
                    {form.name}
                  </p>
                  <p class="text-label text-[color:var(--color-on-surface-variant)] mt-0.5">
                    {form.field_config.length}{" "}
                    {form.field_config.length === 1 ? "Feld" : "Felder"}
                    {targetPhase ? ` · Phase: ${targetPhase.name}` : ""}
                  </p>
                </div>

                {/* Enabled badge / toggle */}
                <button
                  type="button"
                  disabled={!canEdit || isBusy}
                  onClick={() => void toggleEnabled(form.id, form.enabled)}
                  aria-pressed={form.enabled}
                  aria-label={form.enabled ? "Formular deaktivieren" : "Formular aktivieren"}
                  title={form.enabled ? "Aktiv — klicken zum Deaktivieren" : "Inaktiv — klicken zum Aktivieren"}
                  class={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 disabled:opacity-40 ${
                    form.enabled
                      ? "bg-[color:var(--color-primary)]"
                      : "bg-[color:var(--color-surface-container-low)] border border-[color:var(--color-outline-variant)]"
                  }`}
                >
                  <span
                    class={`inline-block h-3 w-3 rounded-full border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] transition-transform ${
                      form.enabled ? "translate-x-3.5" : "translate-x-0.5"
                    }`}
                  />
                </button>

                {/* Rotate-token button (canEdit only) */}
                {canEdit && (
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => void handleRotate(form.id)}
                    title="Link neu erzeugen"
                    aria-label="Link neu erzeugen"
                    class="rounded p-0.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-30 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)]"
                  >
                    <RefreshCw size={14} aria-hidden="true" />
                  </button>
                )}

                {/* Delete button (canEdit only) */}
                {canEdit && (
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() =>
                      setConfirmDel((prev) => {
                        const next = new Set(prev);
                        next.add(form.id);
                        return next;
                      })
                    }
                    class="rounded p-0.5 text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)] disabled:opacity-30 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-error)]"
                    aria-label="Formular löschen"
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                )}
              </div>

              {/* One-time share URL (shown after create or rotate) */}
              {sharedUrl && (
                <div class="border-t border-[color:var(--color-outline-variant)] px-2 py-2 flex flex-col gap-1">
                  <p class="text-label text-[color:var(--color-on-surface-variant)]">
                    Formular-Link (einmalig sichtbar — jetzt kopieren):
                  </p>
                  <ShareLinkDisplay url={sharedUrl} />
                  <button
                    type="button"
                    onClick={() => clearShareUrl(form.id)}
                    class="self-end text-label text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)] underline focus:outline-none"
                  >
                    Ausblenden
                  </button>
                </div>
              )}

              {/* Delete confirm */}
              {isConfirmDel && canEdit && (
                <div class="border-t border-[color:var(--color-outline-variant)] px-2 py-2 flex flex-col gap-1.5">
                  <p class="text-label text-[color:var(--color-on-error-container)] bg-[color:var(--color-error-container)] rounded px-2 py-1.5">
                    Formular wirklich löschen?
                  </p>
                  <div class="flex items-center gap-1.5 justify-end">
                    <button
                      type="button"
                      onClick={() =>
                        setConfirmDel((prev) => {
                          const next = new Set(prev);
                          next.delete(form.id);
                          return next;
                        })
                      }
                      class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                    >
                      Abbrechen
                    </button>
                    <Button
                      variant="destructive"
                      size="xs"
                      type="button"
                      onClick={() => void handleDelete(form.id)}
                      disabled={isBusy}
                    >
                      {isBusy ? "Lösche…" : "Löschen"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

      {/* Add new form */}
      {canEdit && (
        <div class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-hidden">
          {!showNew ? (
            <button
              type="button"
              onClick={() => setShowNew(true)}
              class="w-full inline-flex items-center gap-1.5 px-2 py-1.5 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--color-primary)] transition-colors"
            >
              <Plus size={14} aria-hidden="true" />
              Formular hinzufügen
            </button>
          ) : (
            <div class="px-2 py-2 flex flex-col gap-2">
              <div class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
                Neues Formular
              </div>

              {/* Name */}
              <div class="flex flex-col gap-0.5">
                <label class="text-label text-[color:var(--color-on-surface-variant)]">
                  Name
                </label>
                <input
                  type="text"
                  value={newName}
                  onInput={(e) => setNewName((e.target as HTMLInputElement).value)}
                  placeholder="Formularname…"
                  maxLength={200}
                  class={INPUT_CLS}
                  aria-label="Formularname"
                />
              </div>

              {/* Field config builder */}
              <div class="flex flex-col gap-1">
                <div class="flex items-center justify-between">
                  <span class="text-label text-[color:var(--color-on-surface-variant)]">
                    Felder
                  </span>
                  <button
                    type="button"
                    onClick={addField}
                    class="inline-flex items-center gap-0.5 text-label text-[color:var(--color-primary)] hover:underline focus:outline-none"
                  >
                    <Plus size={14} aria-hidden="true" />
                    Feld hinzufügen
                  </button>
                </div>

                {newFields.map((field, idx) => (
                  <div
                    key={idx}
                    class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2 py-1.5 flex flex-col gap-1"
                  >
                    <div class="flex items-center justify-between gap-1">
                      <span class="text-label font-medium text-[color:var(--color-on-surface-variant)]">
                        Feld {idx + 1}
                      </span>
                      {newFields.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeField(idx)}
                          class="rounded p-0.5 text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-error)]"
                          aria-label={`Feld ${idx + 1} entfernen`}
                        >
                          <Trash2 size={14} aria-hidden="true" />
                        </button>
                      )}
                    </div>

                    <div class="grid grid-cols-2 gap-1">
                      <div class="flex flex-col gap-0.5">
                        <label class="text-label text-[color:var(--color-on-surface-variant)]">
                          Schlüssel
                        </label>
                        <input
                          type="text"
                          value={field.key}
                          onInput={(e) =>
                            updateField(idx, { key: (e.target as HTMLInputElement).value })
                          }
                          placeholder="z. B. name"
                          class={INPUT_CLS}
                          aria-label={`Schlüssel für Feld ${idx + 1}`}
                        />
                      </div>
                      <div class="flex flex-col gap-0.5">
                        <label class="text-label text-[color:var(--color-on-surface-variant)]">
                          Bezeichnung
                        </label>
                        <input
                          type="text"
                          value={field.label}
                          onInput={(e) =>
                            updateField(idx, { label: (e.target as HTMLInputElement).value })
                          }
                          placeholder="z. B. Ihr Name"
                          class={INPUT_CLS}
                          aria-label={`Bezeichnung für Feld ${idx + 1}`}
                        />
                      </div>
                    </div>

                    <div class="flex items-center gap-2">
                      <div class="flex-1 min-w-0 flex flex-col gap-0.5">
                        <label class="text-label text-[color:var(--color-on-surface-variant)]">
                          Typ
                        </label>
                        <select
                          value={field.type}
                          onChange={(e) =>
                            updateField(idx, {
                              type: (e.target as HTMLSelectElement).value as FieldType,
                            })
                          }
                          class={SELECT_CLS}
                          aria-label={`Typ für Feld ${idx + 1}`}
                        >
                          {FIELD_TYPES.map((ft) => (
                            <option key={ft} value={ft}>
                              {FIELD_TYPE_LABELS[ft]}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div class="flex items-center gap-1.5 pt-4">
                        <input
                          type="checkbox"
                          id={`field-required-${idx}`}
                          checked={field.required}
                          onChange={(e) =>
                            updateField(idx, {
                              required: (e.target as HTMLInputElement).checked,
                            })
                          }
                          class="rounded border-[color:var(--color-outline-variant)] text-[color:var(--color-primary)] focus-visible:ring-[color:var(--color-primary)]"
                        />
                        <label
                          for={`field-required-${idx}`}
                          class="text-label text-[color:var(--color-on-surface)] whitespace-nowrap"
                        >
                          Pflichtfeld
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Target phase picker */}
              <div class="flex flex-col gap-0.5">
                <label class="text-label text-[color:var(--color-on-surface-variant)]">
                  Ziel-Phase
                </label>
                {phases.length > 0 ? (
                  <select
                    value={newPhaseId}
                    onChange={(e) => setNewPhaseId((e.target as HTMLSelectElement).value)}
                    class={SELECT_CLS}
                    aria-label="Ziel-Phase"
                  >
                    <option value="">Keine Phase</option>
                    {phases.map((ph) => (
                      <option key={ph.id} value={String(ph.id)}>
                        {ph.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="number"
                    value={newPhaseId}
                    min={1}
                    onInput={(e) => setNewPhaseId((e.target as HTMLInputElement).value)}
                    placeholder="Phasen-ID (optional)"
                    class={INPUT_CLS}
                    aria-label="Ziel-Phase ID"
                  />
                )}
              </div>

              {/* Enabled toggle */}
              <div class="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="new-form-enabled"
                  checked={newEnabled}
                  onChange={(e) => setNewEnabled((e.target as HTMLInputElement).checked)}
                  class="rounded border-[color:var(--color-outline-variant)] text-[color:var(--color-primary)] focus-visible:ring-[color:var(--color-primary)]"
                />
                <label
                  for="new-form-enabled"
                  class="text-xs text-[color:var(--color-on-surface)]"
                >
                  Aktiv
                </label>
              </div>

              <div class="flex items-center gap-1.5 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    resetNewForm();
                    setShowNew(false);
                  }}
                  class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                >
                  Abbrechen
                </button>
                <Button
                  variant="primary"
                  size="xs"
                  type="button"
                  onClick={() => void submitNew()}
                  disabled={!isNewFormSubmittable() || creating}
                >
                  {creating ? "Lege an…" : "Erstellen"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
